import { useState } from 'react';
import type { CardData } from '../game/types';
import { Card } from './Board';

// Tipi mirrored da auth_store (evitare cross-import client/server).
export interface RunMapNode {
  id: string;
  depth: number;
  laneIndex: number;
  type: 'start' | 'regular' | 'extract' | 'boss';
  connections: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  visited: boolean;
  loot: CardData[];
  extractionDisabled?: boolean;
  // Safehouse cache (solo extract): offerte 1-su-3 e carta già presa.
  cacheOffer?: CardData[];
  cacheClaimedId?: string;
}
export interface RunMap {
  nodes: RunMapNode[];
  currentNodeId: string;
}

interface MapViewProps {
  map: RunMap;
  cardsAccCount: number;
  resourcesAcc: Record<string, number>;
  // Click su QUALSIASI nodo → apre l'overlay info (gestito dal parent).
  onNodeClick: (nodeId: string) => void;
}

const LANE_W = 130;
const DEPTH_H = 80;
const TOP_PAD = 30;
const LEFT_PAD = 60;
const NODE_R = 22;

function nodeColor(node: RunMapNode, isCurrent: boolean, isAdjacent: boolean): {
  fill: string; stroke: string; glow: string;
} {
  if (isCurrent) return { fill: '#1a3a3a', stroke: 'var(--accent-color)', glow: 'var(--accent-color)' };
  switch (node.type) {
    case 'start':   return { fill: '#1a1a1a', stroke: '#888',  glow: 'transparent' };
    case 'boss':    return { fill: isAdjacent ? '#3a1a1a' : '#1a1a1a',
                              stroke: '#e57373', glow: isAdjacent ? '#e57373' : 'transparent' };
    case 'extract': return { fill: isAdjacent ? '#3a3a1a' : (node.visited ? '#1a1a1a' : '#1a1a14'),
                              stroke: '#ffb703', glow: isAdjacent ? '#ffb703' : 'transparent' };
    case 'regular':
    default:        return { fill: isAdjacent ? '#1a2a3a' : (node.visited ? '#1a1a1a' : '#16161e'),
                              stroke: isAdjacent ? '#4fc3f7' : '#444',
                              glow: isAdjacent ? '#4fc3f7' : 'transparent' };
  }
}

function nodeLabel(type: RunMapNode['type']): string {
  switch (type) {
    case 'start': return 'S';
    case 'boss':  return 'B';
    case 'extract': return '$';
    default: return '·';
  }
}

export function MapView({
  map, cardsAccCount, resourcesAcc, onNodeClick,
}: MapViewProps) {
  const current = map.nodes.find(n => n.id === map.currentNodeId)!;
  const adjacentIds = new Set(current.connections);

  const maxDepth = Math.max(...map.nodes.map(n => n.depth));
  const width = LEFT_PAD * 2 + LANE_W * 3;
  const height = TOP_PAD * 2 + DEPTH_H * (maxDepth + 1);

  const byId = new Map(map.nodes.map(n => [n.id, n] as const));
  const nodeXY = (n: RunMapNode) => ({
    x: LEFT_PAD + LANE_W * (n.laneIndex + 0.5),
    y: TOP_PAD + DEPTH_H * n.depth + NODE_R,
  });

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {/* Map canvas */}
      <div style={{ background: '#08080d', border: '2px solid var(--accent-color)', borderRadius: 8, padding: 16 }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {/* Connections (linee da ogni nodo al depth+1) */}
          {map.nodes.flatMap(n => n.connections.map(cId => {
            const target = byId.get(cId);
            if (!target) return null;
            const from = nodeXY(n);
            const to = nodeXY(target);
            const isFromCurrent = n.id === current.id;
            return (
              <line
                key={`${n.id}-${cId}`}
                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke={isFromCurrent ? 'var(--accent-color)' : '#333'}
                strokeWidth={isFromCurrent ? 2 : 1}
                strokeDasharray={isFromCurrent ? '0' : '4 4'}
              />
            );
          }))}
          {/* Nodi — TUTTI cliccabili: il parent gestisce l'apertura
              dell'overlay info che mostra loot + (se adiacente) deck picker + go. */}
          {map.nodes.map(n => {
            const { x, y } = nodeXY(n);
            const isCurrent = n.id === current.id;
            const isAdj = adjacentIds.has(n.id);
            const colors = nodeColor(n, isCurrent, isAdj);
            const hasLoot = n.loot && n.loot.length > 0;
            return (
              <g
                key={n.id}
                onClick={() => onNodeClick(n.id)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={x} cy={y} r={NODE_R}
                  fill={colors.fill}
                  stroke={colors.stroke}
                  strokeWidth={isCurrent ? 3 : 2}
                  style={{ filter: colors.glow !== 'transparent' ? `drop-shadow(0 0 6px ${colors.glow})` : 'none' }}
                />
                <text
                  x={x} y={y + 5} textAnchor="middle"
                  style={{
                    fill: isCurrent ? 'var(--accent-color)' : (isAdj ? '#fff' : '#888'),
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                  }}
                >{nodeLabel(n.type)}</text>
                {isCurrent && (
                  <text
                    x={x} y={y - NODE_R - 6} textAnchor="middle"
                    style={{ fill: 'var(--accent-color)', fontSize: 10, fontFamily: 'inherit', pointerEvents: 'none' }}
                  >YOU</text>
                )}
                {hasLoot && (
                  <g style={{ pointerEvents: 'none' }}>
                    <circle
                      cx={x + NODE_R - 4} cy={y - NODE_R + 4} r={8}
                      fill="#ffb703" stroke="#000" strokeWidth={1}
                    />
                    <text
                      x={x + NODE_R - 4} y={y - NODE_R + 7} textAnchor="middle"
                      style={{ fill: '#000', fontSize: 9, fontWeight: 'bold', fontFamily: 'inherit' }}
                    >{n.loot.length}</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
        <div style={{ marginTop: 10, display: 'flex', gap: 16, color: '#888', fontSize: 11 }}>
          <span><strong style={{ color: '#4fc3f7' }}>·</strong> Regular</span>
          <span><strong style={{ color: '#ffb703' }}>$</strong> Extract</span>
          <span><strong style={{ color: '#e57373' }}>B</strong> Boss</span>
          <span><strong style={{ color: 'var(--accent-color)' }}>●</strong> You are here</span>
        </div>
      </div>

      {/* Sidebar loot accumulato */}
      <div className="run-loot-sidebar" style={{ background: '#0e0e16', border: '1px solid #444', borderRadius: 6, padding: '12px 14px', minWidth: 200 }}>
        <h3 style={{ color: 'var(--accent-color)', fontSize: 13, marginTop: 0, marginBottom: 8 }}>Run loot</h3>
        <div style={{ color: '#ccc', fontSize: 12, lineHeight: 1.6 }}>
          <div><strong>Cards:</strong> {cardsAccCount}</div>
          <div><strong>Resources:</strong></div>
          {Object.keys(resourcesAcc).length === 0
            ? <div style={{ color: '#666', fontSize: 11 }}>none</div>
            : Object.entries(resourcesAcc).map(([s, n]) => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={`suit-icon suit-icon-${s.replace(/\s+/g, '-')}`} style={{ width: 14, height: 14 }} />
                  <span>{s} ×{n}</span>
                </div>
              ))}
        </div>
        <p style={{ color: '#666', fontSize: 10, marginTop: 12, fontStyle: 'italic' }}>
          Extract at a <span style={{ color: '#ffb703' }}>$</span> node to bank your loot.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Extract action overlay: si apre quando il giocatore è SU un extract node.
// Opzioni: Estrai (POST extract → run extracted) / Riconfigura (stub M2) / Continua.

interface ExtractActionOverlayProps {
  cardsAcc: CardData[];
  resourcesAcc: Record<string, number>;
  cacheOffer: CardData[];
  cacheClaimedId: string | null;
  onClaimCache: (index: number) => void;
  onExtract: () => void;
  onReconfigure: () => void;
  onClose: () => void;
  busy: boolean;
}

export function ExtractActionOverlay({
  cardsAcc, resourcesAcc, cacheOffer, cacheClaimedId, onClaimCache, onExtract, onReconfigure, onClose, busy,
}: ExtractActionOverlayProps) {
  // Selezione esplicita + conferma, per evitare prese accidentali.
  const [picked, setPicked] = useState<number | null>(null);
  const claimedCard = cacheClaimedId ? cardsAcc.find(c => c.id === cacheClaimedId) : undefined;
  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ color: '#ffb703', margin: 0 }}>🎰 Extraction Node</h2>
        <p style={{ color: '#aaa', fontSize: 13, marginTop: 8 }}>
          You're at a safe exit. You can extract now and bank all accumulated loot,
          or push deeper into the map for more rewards (and more risk).
        </p>

        {/* Safehouse cache: scegli 1 carta su 3 */}
        {(cacheOffer.length > 0 || claimedCard) && (
          <div style={{ marginTop: 16, padding: 12, border: '1px solid #ffb703', borderRadius: 8, background: '#1a1608' }}>
            <div style={{ color: '#ffb703', fontWeight: 'bold', fontSize: 13 }}>
              🗝 Safehouse cache {claimedCard ? '— taken' : '— pick 1 of 3'}
            </div>
            <div style={{ color: '#aaa', fontSize: 11, marginTop: 4, marginBottom: 10 }}>
              {claimedCard
                ? 'Added to your run loot. Extract to keep it for good.'
                : 'Choose one card for your run loot. You keep it if you extract, you lose it on a forced exit.'}
            </div>
            {claimedCard ? (
              <Card card={claimedCard} staticContext />
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  {cacheOffer.map((c, i) => (
                    <button
                      key={c.id}
                      onClick={() => setPicked(i)}
                      disabled={busy}
                      title="Select this card"
                      style={{
                        background: 'transparent',
                        font: 'inherit', // il <button> non eredita il font: senza, il testo effetto della carta rimpicciolisce
                        border: `3px solid ${picked === i ? '#ffb703' : 'transparent'}`,
                        borderRadius: 10,
                        padding: 3,
                        cursor: busy ? 'wait' : 'pointer',
                        boxShadow: picked === i ? '0 0 14px rgba(255, 183, 3, 0.6)' : 'none',
                      }}
                    >
                      <Card card={c} staticContext />
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                  <button
                    onClick={() => picked !== null && onClaimCache(picked)}
                    disabled={picked === null || busy}
                    style={{ ...btnPrimaryStyle, opacity: picked === null || busy ? 0.5 : 1, cursor: picked === null || busy ? 'not-allowed' : 'pointer' }}
                  >
                    {picked === null ? 'Select a card' : 'Take this card'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, marginTop: 20, marginBottom: 24 }}>
          <div style={summaryBoxStyle}>
            <div style={{ color: 'var(--accent-color)', fontWeight: 'bold', fontSize: 12 }}>Cards to bank</div>
            <div style={{ color: '#ccc', fontSize: 18, marginTop: 4 }}>{cardsAcc.length}</div>
            {cardsAcc.length > 0 && (
              <div style={{ color: '#888', fontSize: 10, marginTop: 6, maxWidth: 220 }}>
                {summarizeCards(cardsAcc)}
              </div>
            )}
          </div>
          <div style={summaryBoxStyle}>
            <div style={{ color: 'var(--accent-color)', fontWeight: 'bold', fontSize: 12 }}>Resources</div>
            <div style={{ color: '#ccc', fontSize: 12, marginTop: 6 }}>
              {Object.keys(resourcesAcc).length === 0
                ? <span style={{ color: '#666' }}>none</span>
                : Object.entries(resourcesAcc).map(([s, n]) => (
                    <div key={s}>{s}: {n}</div>
                  ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} disabled={busy} style={btnNeutralStyle}>
            Continue the run
          </button>
          <button onClick={onReconfigure} disabled={busy} style={{ ...btnNeutralStyle, color: 'var(--accent-color)', border: '1px solid var(--accent-color)' }}>
            🛠 Reconfigure decks
          </button>
          <button
            onClick={onExtract}
            disabled={busy}
            style={btnPrimaryStyle}
          >
            {busy ? 'Extracting...' : '💰 Extract & end'}
          </button>
        </div>
      </div>
    </div>
  );
}

function summarizeCards(cards: CardData[]): string {
  const counts = new Map<string, number>();
  for (const c of cards) {
    const k = c.type === 'Hand' ? `${c.suit} ${c.value}` : `${c.suit} mission`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([k, n]) => n > 1 ? `${k}×${n}` : k)
    .join(', ');
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.8)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};

const panelStyle: React.CSSProperties = {
  background: '#0e0e16',
  border: '2px solid #ffb703',
  borderRadius: 10,
  padding: 24,
  maxWidth: 640,
  width: '100%',
  boxShadow: '0 0 20px rgba(255, 183, 3, 0.3)',
};

const summaryBoxStyle: React.CSSProperties = {
  flex: 1,
  background: '#1a1a24',
  border: '1px solid #333',
  borderRadius: 6,
  padding: '10px 12px',
};

const btnPrimaryStyle: React.CSSProperties = {
  background: '#ffb703',
  border: 'none',
  color: '#000',
  padding: '10px 22px',
  borderRadius: 6,
  fontWeight: 'bold',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnNeutralStyle: React.CSSProperties = {
  background: '#333',
  border: 'none',
  color: '#bbb',
  padding: '10px 22px',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ---------------------------------------------------------------
// NodeInfoOverlay: click su un qualsiasi nodo della mappa apre questo.
// Mostra info nodo + loot disponibile. Se il nodo è adiacente al current,
// aggiunge il deck picker A/B + bottone "Parti per questo nodo".
// Se non è adiacente, è solo inspection.

interface NodeInfoOverlayProps {
  node: RunMapNode;
  isAdjacent: boolean;
  isCurrent: boolean;
  decksSnapshot: {
    A: { name: string; valid: boolean; cards: CardData[] };
    B: { name: string; valid: boolean; cards: CardData[] };
  };
  busy: boolean;
  onClose: () => void;
  onPlayMatch: (deckKey: 'A' | 'B') => void;     // regular/boss
  onAdvanceToExtract: () => void;                 // extract: advance senza match
  onOpenExtractActions?: () => void;              // current === extract: riapre action panel
}

export function NodeInfoOverlay({
  node, isAdjacent, isCurrent, decksSnapshot, busy,
  onClose, onPlayMatch, onAdvanceToExtract, onOpenExtractActions,
}: NodeInfoOverlayProps) {
  // Selezione esplicita del mazzo: click su un mazzo seleziona (highlight),
  // poi serve premere "Parti" per avviare il match. Evita partenze accidentali.
  // Default: se UN solo mazzo è valido, lo pre-seleziona per ridurre i click.
  const validKeys = (['A', 'B'] as const).filter(k => decksSnapshot[k].valid);
  const defaultSelected: 'A' | 'B' | null = validKeys.length === 1 ? validKeys[0] : null;
  const [selectedDeck, setSelectedDeck] = useState<'A' | 'B' | null>(defaultSelected);
  const typeLabel = ({
    start: 'Starting point',
    regular: 'Regular node',
    extract: 'Extraction node',
    boss: 'Boss node',
  } as const)[node.type];

  const difficultyColor = node.difficulty === 'easy' ? '#4ade80'
                        : node.difficulty === 'medium' ? '#fbbf24'
                        : '#e57373';

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...panelStyle, borderColor: 'var(--accent-color)', boxShadow: '0 0 18px rgba(0, 200, 255, 0.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ color: 'var(--accent-color)', margin: 0 }}>{typeLabel}</h2>
            <p style={{ color: '#888', fontSize: 12, marginTop: 4, marginBottom: 0 }}>
              Depth {node.depth} · Lane {node.laneIndex} · Difficulty <span style={{ color: difficultyColor }}>{node.difficulty}</span>
              {node.visited && <span style={{ color: '#4ade80', marginLeft: 8 }}>· ✓ visited</span>}
              {isCurrent && <span style={{ color: 'var(--accent-color)', marginLeft: 8 }}>· you are here</span>}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        {/* Loot del nodo */}
        <div style={{ marginTop: 16 }}>
          {node.type === 'extract' ? (
            <h3 style={{ color: '#ffb703', fontSize: 13, marginBottom: 6 }}>
              {node.cacheClaimedId
                ? 'Safehouse cache: already taken'
                : node.cacheOffer && node.cacheOffer.length > 0
                  ? 'Safehouse cache: pick 1 of 3 cards (open the extraction actions)'
                  : 'Safehouse cache: pick 1 of 3 cards when you arrive'}
            </h3>
          ) : (
            <h3 style={{ color: '#ffb703', fontSize: 13, marginBottom: 6 }}>
              Loot at this node: {node.loot.length === 0 ? <span style={{ color: '#666' }}>empty</span> : `${node.loot.length} cards`}
            </h3>
          )}
          {node.loot.length > 0 && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {node.loot.map((c, i) => (
                  <div key={i}><Card card={c} staticContext /></div>
                ))}
              </div>
              <p style={{ color: '#aaa', fontSize: 11, fontStyle: 'italic', marginTop: 8 }}>
                Win a match here to recover this loot.
              </p>
            </>
          )}
        </div>

        {/* Actions */}
        <div style={{ marginTop: 24, borderTop: '1px solid #333', paddingTop: 16 }}>
          {isCurrent && node.type === 'extract' && !node.extractionDisabled && onOpenExtractActions && (
            <button onClick={onOpenExtractActions} disabled={busy} style={{ ...btnPrimaryStyle, background: '#ffb703' }}>
              🎰 Open extraction actions
            </button>
          )}
          {isCurrent && node.type === 'extract' && node.extractionDisabled && (
            <p style={{ color: '#e57373', fontSize: 12, margin: '0 0 12px 0', fontStyle: 'italic', lineHeight: 1.4 }}>
              ⚠️ Extraction is no longer available at this safehouse because you decided to continue the run.
            </p>
          )}
          {isAdjacent && node.type === 'extract' && (
            <div>
              <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 12px 0' }}>
                Adjacent extraction node. Move here without a match to access extraction actions.
              </p>
              <button onClick={onAdvanceToExtract} disabled={busy} style={btnPrimaryStyle}>
                {busy ? 'Moving...' : '→ Go to extraction node'}
              </button>
            </div>
          )}
          {isAdjacent && (node.type === 'regular' || node.type === 'boss') && (
            <div>
              <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 12px 0' }}>
                Adjacent {node.type === 'boss' ? 'boss' : 'regular'} node — select the deck to fight with, then press <strong>Start</strong>.
              </p>
              <p style={{ color: '#fbbf24', fontSize: 11, margin: '0 0 12px 0' }}>
                ⚠ Once started, the match must be finished. Forfeiting it, or leaving it untouched for over an hour, counts as a loss.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                {(['A', 'B'] as const).map(k => {
                  const slot = decksSnapshot[k];
                  const selected = selectedDeck === k;
                  const borderColor = !slot.valid ? '#444'
                                    : selected ? 'var(--accent-color)' : '#666';
                  const bg = !slot.valid ? '#222'
                           : selected ? '#1a3a4a' : '#15151c';
                  return (
                    <button
                      key={k}
                      disabled={!slot.valid || busy}
                      onClick={() => setSelectedDeck(k)}
                      style={{
                        flex: 1,
                        background: bg,
                        border: `2px solid ${borderColor}`,
                        color: slot.valid ? (selected ? 'var(--accent-color)' : '#bbb') : '#555',
                        padding: '14px',
                        borderRadius: 6,
                        cursor: slot.valid && !busy ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        boxShadow: selected ? '0 0 10px rgba(0, 200, 255, 0.4)' : 'none',
                        transition: 'all 0.12s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {selected && <span style={{ color: 'var(--accent-color)' }}>●</span>}
                        <div style={{ fontWeight: 'bold', fontSize: 14 }}>Deck {k}: {slot.name}</div>
                      </div>
                      <div style={{ fontSize: 11, marginTop: 4, color: slot.valid ? '#4ade80' : '#e57373' }}>
                        {slot.valid ? `✓ ${slot.cards.length}/14 valid` : `✗ invalid (${slot.cards.length}/14)`}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  disabled={selectedDeck === null || busy}
                  onClick={() => selectedDeck && onPlayMatch(selectedDeck)}
                  style={{
                    background: selectedDeck && !busy ? 'var(--accent-color)' : '#333',
                    border: 'none',
                    color: selectedDeck && !busy ? '#000' : '#666',
                    padding: '10px 28px',
                    borderRadius: 6,
                    fontFamily: 'inherit',
                    fontWeight: 'bold',
                    fontSize: 14,
                    cursor: selectedDeck && !busy ? 'pointer' : 'not-allowed',
                  }}
                  title={selectedDeck === null ? 'Pick a deck first' : `Start with deck ${selectedDeck}`}
                >
                  {busy ? 'Starting...' : selectedDeck === null ? 'Pick a deck' : `→ Start with Deck ${selectedDeck}`}
                </button>
              </div>
            </div>
          )}
          {!isAdjacent && !isCurrent && (
            <p style={{ color: '#666', fontSize: 12, fontStyle: 'italic', margin: 0 }}>
              This node isn't reachable right now. Inspection only.
            </p>
          )}
          {isCurrent && node.type !== 'extract' && (
            <p style={{ color: '#666', fontSize: 12, fontStyle: 'italic', margin: 0 }}>
              You're already on this node. Pick an adjacent one to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


